import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { useDeleteOverride, useOverrides, useUpsertOverride } from '../../api/queries';

export default function AdminOverrides() {
  const { data, isLoading, error } = useOverrides();
  const upsert = useUpsertOverride();
  const del = useDeleteOverride();
  const { enqueueSnackbar } = useSnackbar();
  const [userEmail, setUserEmail] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [managerName, setManagerName] = useState('');

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed: {String(error)}</Alert>;

  const save = async () => {
    if (!userEmail.trim() || !managerEmail.trim()) return;
    try {
      await upsert.mutateAsync({
        user_email: userEmail.trim().toLowerCase(),
        manager_email: managerEmail.trim().toLowerCase(),
        manager_name: managerName.trim() || null,
      });
      enqueueSnackbar('Override saved', { variant: 'success' });
      setUserEmail('');
      setManagerEmail('');
      setManagerName('');
    } catch (err) {
      enqueueSnackbar(`Failed: ${err instanceof Error ? err.message : err}`, { variant: 'error' });
    }
  };

  const remove = async (email: string) => {
    try {
      await del.mutateAsync(email);
      enqueueSnackbar('Override removed', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Failed: ${err instanceof Error ? err.message : err}`, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary" className="max-w-2xl">
        Overrides pin who gets CC&apos;d as the manager on access-request emails — they beat the
        built-in org chart. Use when the chart resolves the wrong person.
      </Typography>

      <Stack direction="row" spacing={1} className="max-w-3xl">
        <TextField
          label="Employee email"
          size="small"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          className="flex-1"
        />
        <TextField
          label="Manager email"
          size="small"
          value={managerEmail}
          onChange={(e) => setManagerEmail(e.target.value)}
          className="flex-1"
        />
        <TextField
          label="Manager name (optional)"
          size="small"
          value={managerName}
          onChange={(e) => setManagerName(e.target.value)}
          className="flex-1"
        />
        <Button
          variant="contained"
          onClick={save}
          disabled={upsert.isPending || !userEmail.trim() || !managerEmail.trim()}
        >
          Save
        </Button>
      </Stack>

      {data && data.length > 0 ? (
        <Table size="small" className="max-w-3xl">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Manager name</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((o) => (
              <TableRow key={o.user_email} hover>
                <TableCell>{o.user_email}</TableCell>
                <TableCell>{o.manager_email}</TableCell>
                <TableCell>{o.manager_name ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => remove(o.user_email)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary">No overrides set.</Typography>
      )}
    </Stack>
  );
}
